import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { CommonModule } from '@angular/common';
import { LOCALE_ID } from '@angular/core';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AngularResizeEventModule } from 'angular-resize-event';
import { CloseableContainerComponent } from 'app/modules/main/components/closeable-container/closeable-container.component';
import { ContentEditorComponent } from 'app/modules/main/components/content-editor/content-editor.component';
import { EmojiPickerCloser } from 'app/modules/main/components/content-editor/emoji-picker-closer.directive';
import { DataSelectorComponent } from 'app/modules/main/components/data-selector/data-selector.component';
import { DateTimePickerComponent } from 'app/modules/main/components/date-time-picker/date-time-picker.component';
import { ErrorPopupComponent } from 'app/modules/main/components/error-popup/error-popup.component';
import { GenericFormContainerComponent } from 'app/modules/main/components/generic-form-container/generic-form-container.component';
import { OfferSelectorComponent } from 'app/modules/main/components/offer-selector/offer-selector.component';
import { PaginationComponent } from 'app/modules/main/components/pagination/pagination.component';
import { PromotionalTypeSelectorComponent } from 'app/modules/main/components/promotional-type-selector/promotional-type-selector.component';
import { PublisherSelectorComponent } from 'app/modules/main/components/publisher-selector/publisher-selector.component';
import { PublisherTagSelectorComponent } from 'app/modules/main/components/publisher-tag-selector/publisher-tag-selector.component';
import { SecureImageComponent } from 'app/modules/main/components/secure-image/secure-image.component';
import { TableControlsComponent } from 'app/modules/main/components/table-controls/table-controls.component';
import { OverflowMarker } from 'app/modules/main/directives/overflow-marker.directive';
import { AccountDatePipe } from 'app/modules/main/pipes/account-date-pipe';
import { AdvertiserNamePipe } from 'app/modules/main/pipes/advertiser-name-pipe';
import { DisplayCasePipe } from 'app/modules/main/pipes/display-case-pipe';
import { OAuthStorageService } from 'app/modules/main/services/oauth-storage.service';
import { AdvertiserTimeZoneEffects } from 'app/modules/main/store/advertiser-time-zone/advertiser-time-zone.effects';
import { AdvertiserTimeZoneReducer } from 'app/modules/main/store/advertiser-time-zone/advertiser-time-zone.reducer';
import { AdvertiserTimeZoneSelectors } from 'app/modules/main/store/advertiser-time-zone/advertiser-time-zone.selectors';
import { PublisherTimeZoneEffects } from 'app/modules/main/store/publisher-time-zone/publisher-time-zone.effects';
import { PublisherTimeZoneReducer } from 'app/modules/main/store/publisher-time-zone/publisher-time-zone.reducer';
import { PublisherTimeZoneSelectors } from 'app/modules/main/store/publisher-time-zone/publisher-time-zone.selectors';
import { UserAuthorizationActions } from 'app/modules/main/store/user-authorization/user-authorization.actions';
import { UserAuthorizationEffects } from 'app/modules/main/store/user-authorization/user-authorization.effects';
import { UserAuthorizationReducer } from 'app/modules/main/store/user-authorization/user-authorization.reducer';
import { UserAuthorizationSelectors } from 'app/modules/main/store/user-authorization/user-authorization.selectors';
import { UserSettingsEffects } from 'app/modules/main/store/user-settings/user-settings.effects';
import { UserSettingsReducer } from 'app/modules/main/store/user-settings/user-settings.reducer';
import { UserSettingsSelectors } from 'app/modules/main/store/user-settings/user-settings.selectors';
import { DialogsModule, PopupService } from '@awin/shared/dialogs';
import { NgxPaginationModule } from 'ngx-pagination';
import { QuillModule } from 'ngx-quill';

@NgModule({
  declarations: [
    AccountDatePipe,
    AdvertiserNamePipe,
    CloseableContainerComponent,
    ContentEditorComponent,
    DataSelectorComponent,
    DateTimePickerComponent,
    DisplayCasePipe,
    EmojiPickerCloser,
    ErrorPopupComponent,
    GenericFormContainerComponent,
    OfferSelectorComponent,
    OverflowMarker,
    PaginationComponent,
    PromotionalTypeSelectorComponent,
    PublisherSelectorComponent,
    PublisherTagSelectorComponent,
    SecureImageComponent,
    TableControlsComponent,
  ],
  exports: [
    AccountDatePipe,
    AdvertiserNamePipe,
    CloseableContainerComponent,
    ContentEditorComponent,
    DataSelectorComponent,
    DateTimePickerComponent,
    DisplayCasePipe,
    ErrorPopupComponent,
    GenericFormContainerComponent,
    OfferSelectorComponent,
    OverflowMarker,
    PaginationComponent,
    PromotionalTypeSelectorComponent,
    PublisherSelectorComponent,
    PublisherTagSelectorComponent,
    SecureImageComponent,
    TableControlsComponent,
  ],
  imports: [
    AngularResizeEventModule,
    CommonModule,
    DialogsModule,
    DragDropModule,
    EffectsModule.forFeature([
      AdvertiserTimeZoneEffects,
      PublisherTimeZoneEffects,
      UserAuthorizationEffects,
      UserSettingsEffects,
    ]),
    FormsModule,
    NgbModule,
    NgxPaginationModule,
    QuillModule.forRoot({
      format: 'html',
    }),
    ReactiveFormsModule,
    RouterModule,
    ScrollingModule,
    StoreModule.forFeature(
      AdvertiserTimeZoneSelectors.FEATURE,
      AdvertiserTimeZoneReducer.reducer(),
    ),
    StoreModule.forFeature(
      PublisherTimeZoneSelectors.FEATURE,
      PublisherTimeZoneReducer.reducer(),
    ),
    StoreModule.forFeature(
      UserAuthorizationSelectors.FEATURE,
      UserAuthorizationReducer.reducer(),
    ),
    StoreModule.forFeature(
      UserSettingsSelectors.FEATURE,
      UserSettingsReducer.reducer(),
    ),
  ],
  providers: [
    PopupService,
    {
      deps: [LOCALE_ID],
      provide: DatePipe,
      useFactory: (locale: string) => new DatePipe(locale),
    },
    {
      multi: true,
      provide: AdvertiserTimeZoneEffects.LOAD,
      useValue: UserAuthorizationActions.userAuthorizedForAdvertiserAccount,
    },
    {
      multi: true,
      provide: PublisherTimeZoneEffects.LOAD,
      useValue: UserAuthorizationActions.userAuthorizedForPublisherAccount,
    },
    {
      multi: true,
      provide: UserSettingsEffects.LOAD,
      useValue: [
        UserAuthorizationActions.userAuthorizedForAdvertiserAccount,
        UserAuthorizationActions.userAuthorizedForPublisherAccount,
      ]
    },
  ],
})
export class MainModule {
}
